import type { ReassignmentRequest, ReassignmentResponse } from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchReassignmentApi } from "../../../../../api/client";
import { applicationStatusKeys } from "../../../api/applicationStatusKeys";
import { dscWorkbenchKeys } from "../../../api/dscWorkbenchKeys";
import { reassignmentKeys } from "./reassignmentKeys";

/**
 * Saves the Deal Status reassignment (BFF `POST .../reassignment`). `expectedVersion` is never
 * sent (optional; CAS has no status version). A save from RAP can flip the deal to RAC, so on
 * success the saved record, dashboard (milestone dates) and application status are invalidated.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} [roleName] - The caller's department role name.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSaveReassignment(approvalId: string, roleName?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ReassignmentRequest): Promise<ReassignmentResponse> => {
      const response = await workbenchReassignmentApi.saveReassignment(approvalId, request, roleName);
      return response.data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: reassignmentKeys.detail({ approvalId }) }),
        queryClient.invalidateQueries({ queryKey: dscWorkbenchKeys.detail({ approvalId }) }),
        queryClient.invalidateQueries({ queryKey: applicationStatusKeys.detail({ approvalId }) }),
      ]);
    },
  });
}
